import React, { useEffect, useMemo } from "react";
import type { SetStateAction, Dispatch } from "react";
import { Button, Checkbox, DatePicker, Form, Input, Modal, Select, Tooltip } from "antd";
import type { FormInstance } from "antd";
import type { Moment } from "moment";
import MultipleSelect from "../../common/MultipleSelect";
import InputNumberFormat from "../../common/InputNumberFormat";
import { LoadServiceData } from "../types/LoadServiceData";
import { Agreement } from "../../../lib/types/Agreement";
import { DictionaryDto } from "../../../lib/interfaces";
import { selectFilterOptionWithChildren } from "../../../lib/utils/selectFilterOption";
import { IProduct } from "../../../lib/types";
import { displayFormat } from "../../../lib/utils/formatDate";
import { FormInfo } from "../../common/FormInfo/FormInfo";
import "./CreateAdjustmentModal.less";

interface FormData {
  agreementId?: number;
  serviceId?: number;
  selectedStatuses?: number[];
  period: [Moment, Moment] | null;
  amount?: string;
  chargeName?: string;
  dsc?: string;
  includePastDates?: boolean;
}

interface ComponentProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  agreements: Agreement[];
  form: FormInstance<FormData>;
  setLoadServicesData: Dispatch<SetStateAction<LoadServiceData>>;
  agrStatus: DictionaryDto<number>[];
  isServicesLoading: boolean;
  defaultSelectedStatuses: number[];
  serviceForAdjustmentCreate?: IProduct[];
  getTooltipTitle: (item: any) => JSX.Element;
}

export const CreateAdjustmentModal = (props: ComponentProps) => {
  const {
    visible,
    onClose,
    onSubmit,
    agreements,
    form,
    setLoadServicesData,
    agrStatus,
    isServicesLoading,
    defaultSelectedStatuses,
    getTooltipTitle,
    serviceForAdjustmentCreate,
  } = props;

  const initialValues = useMemo<FormData>(() => {
    return {
      agreementId: agreements?.length === 1 ? agreements[0].id : undefined,
      serviceId: undefined,
      selectedStatuses: defaultSelectedStatuses,
      period: null,
      amount: undefined,
      chargeName: undefined,
      dsc: undefined,
      includePastDates: false,
    };
  }, [defaultSelectedStatuses, agreements]);

  // Установка исходных значений формы по открытии модалки
  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [visible, initialValues]);

  // Передача данных для загрузки услуг, при единственности договора
  useEffect(() => {
    if (visible && agreements?.length === 1) {
      setLoadServicesData((prev) => ({
        ...prev,
        agreementId: agreements[0].id,
      }));
    }
  }, [agreements, visible]);

  return (
    <Modal
      title="Создание корректировки"
      visible={visible}
      onCancel={onClose}
      width={750}
      centered
      className="create-adjustment-modal"
      footer={
        <>
          <Button className="create-adjustment-modal__btn" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="create-adjustment-modal__btn"
            type="primary"
            onClick={form.submit}
            disabled={isServicesLoading}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <Form
        name="dynamic_form_nest_item"
        form={form}
        autoComplete="off"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        onFinish={onSubmit}
        initialValues={initialValues}
      >
        <Form.Item noStyle dependencies={["selectedStatuses"]}>
          {({ getFieldValue }) => {
            const selectedStatuses = getFieldValue("selectedStatuses") || [];
            return (
              <Form.Item
                label="Номер договора"
                name="agreementId"
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  onChange={(agreementId: number) => {
                    setLoadServicesData((prev) => ({
                      ...prev,
                      agreementId,
                    }));
                    form.setFieldsValue({ serviceId: undefined, period: null });
                  }}
                  loading={isServicesLoading}
                  disabled={isServicesLoading}
                  showSearch
                  filterOption={selectFilterOptionWithChildren}
                >
                  {agreements &&
                    agreements.length !== 0 &&
                    agreements
                      .filter(({ agreementStatusId }) => {
                        if (selectedStatuses.length === 0) {
                          return true;
                        }
                        return selectedStatuses.includes(agreementStatusId);
                      })
                      .map((item2) => (
                        <Select.Option key={item2.id} value={item2.id}>
                          {item2.agreementNumber}
                        </Select.Option>
                      ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item label="Статус договора" name="selectedStatuses">
          <MultipleSelect
            onChange={(values) => {
              const statuses = values.map(Number);
              const agreementId: number | undefined = form.getFieldValue("agreementId");
              const agreementStatusId = agreements.find(({ id }) => id === agreementId)
                ?.agreementStatusId;
              if (statuses.length && agreementStatusId && !statuses.includes(agreementStatusId)) {
                form.setFieldsValue({ agreementId: undefined, serviceId: undefined });
                setLoadServicesData((prev) => ({
                  ...prev,
                  agreementId: undefined,
                }));
              }
            }}
            allValues={agrStatus?.map(({ id }) => id) || []}
            placeholder="Статус"
            style={{ height: "30px" }}
            allowClear
            maxTagCount={1}
            showSearch
            filterOption={selectFilterOptionWithChildren}
            disabled={isServicesLoading}
          >
            {[...agrStatus]
              .sort((a, b) => (a.id > b.id ? 1 : -1))
              .map(({ id, name }) => (
                <Select.Option key={id} value={id}>
                  {name}
                </Select.Option>
              ))}
          </MultipleSelect>
        </Form.Item>
        <Form.Item noStyle dependencies={["agreementId"]}>
          {({ getFieldValue }) => {
            const agreementId: number | undefined = getFieldValue("agreementId");
            const placeholder = (() => {
              if (agreementId) {
                return isServicesLoading ? "Идет загрузка продуктов..." : undefined;
              }
              return "Необходимо выбрать договор";
            })();
            return (
              <Form.Item label="Продукт" name="serviceId">
                <Select
                  allowClear
                  loading={isServicesLoading}
                  showSearch
                  filterOption={selectFilterOptionWithChildren}
                  onChange={() => {
                    form.setFieldsValue({ period: null });
                  }}
                  disabled={!agreementId || isServicesLoading}
                  placeholder={placeholder}
                >
                  {serviceForAdjustmentCreate &&
                    serviceForAdjustmentCreate
                      .filter((item) => item.serviceStatusId !== 5)
                      .map((item, idx) => {
                        const name = `${item.id} - ${item?.serviceType?.name}${
                          item?.offering?.extName ? ` - ${item?.offering?.extName}` : ""
                        }`;
                        return (
                          <Select.Option key={idx} value={item?.id}>
                            <Tooltip
                              overlayInnerStyle={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                padding: "0",
                              }}
                              title={getTooltipTitle(item)}
                              placement="topLeft"
                            >
                              {name}
                            </Tooltip>
                          </Select.Option>
                        );
                      })}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={["agreementId"]}>
          {({ getFieldValue }) => {
            const agreementId: number | undefined = getFieldValue("agreementId");
            return (
              <Form.Item name="includePastDates" valuePropName="checked">
                <Checkbox
                  onChange={(event) => {
                    const includePastDates = event.target.checked;
                    setLoadServicesData((prev) => ({
                      ...prev,
                      includePastDates,
                    }));
                    form.setFieldsValue({ serviceId: undefined, period: null });
                  }}
                  disabled={!agreementId || isServicesLoading}
                >
                  Только закрытые
                </Checkbox>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item noStyle dependencies={["agreementId", "serviceId"]}>
          {({ getFieldValue }) => {
            const serviceId: number | undefined = getFieldValue("serviceId");
            const agreementId: number | undefined = getFieldValue("agreementId");
            const disablePeriod = (date: Moment) => {
              if (serviceId) {
                const service = serviceForAdjustmentCreate?.find(({ id }) => id === serviceId);
                if (service) {
                  return !date.isBetween(service.fromDate, service.toDate, "day", "[]");
                }
              }
              if (agreementId) {
                const agreement = agreements?.find(({ id }) => id === agreementId);
                if (agreement) {
                  return !date.isBetween(agreement.fromDate, agreement.toDate, "day", "[]");
                }
              }
              return false;
            };
            return (
              <Form.Item label="Период" name="period" rules={[{ required: true, message: "" }]}>
                <DatePicker.RangePicker format={displayFormat} disabledDate={disablePeriod} />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          label="Сумма с НДС"
          name="amount"
          rules={[
            {
              required: true,
              message: "",
            },
            () => ({
              validator(rule, value) {
                if (value >= 0.01) {
                  return Promise.resolve();
                }
                const message =
                  value === "" || value === undefined ? "" : "Сумма должна быть не менее 0.01";
                return Promise.reject(message);
              },
            }),
          ]}
        >
          <InputNumberFormat />
        </Form.Item>
        <Form.Item label="Номенклатура" name="chargeName">
          <Input />
        </Form.Item>
        <Form.Item label="Комментарий" name="dsc" labelAlign="left">
          <Input.TextArea />
        </Form.Item>
      </Form>
      <FormInfo getFormValues={() => form.getFieldsValue()} />
    </Modal>
  );
};
